/**
 * Asking an event line to run on one pull request (spec edge cases 11 and 12).
 *
 * "Run now" on a clock or queue line just starts the next run. On an event
 * line it has to name the event's subject first, so this is the naming: a
 * number, or a pasted pull request URL where the endpoint accepts one, plus
 * the repository when the line covers more than one.
 *
 * The repository is validated against the LINE's filter rather than the Team's
 * scope, because the line is what would do the work: asking Proctor's
 * `shared-svc` line about a `pricing` pull request is a request nothing would
 * ever answer, and hearing that now beats hearing it from a run that skipped.
 */

import { useMemo, useState } from "react";

import type { JobKindWire } from "@yagni/shared";

import {
  ASK_CANCEL,
  ASK_PR_LABEL,
  ASK_PR_PLACEHOLDER_NUMBER,
  ASK_PR_PLACEHOLDER_URL,
  ASK_REPO_LABEL,
  ASK_SENDING,
  ASK_SENT,
  askNotThisLinesRepo,
  askUnreadable,
} from "./copy.js";
import {
  lineRepos,
  parsePrNumber,
  parseRepoFromUrl,
  pullRequestAskFor,
} from "./pullRequestAsk.js";

export interface RunNowPickerProps {
  kind: JobKindWire;
  deskId: string;
  /** The line's filter as it stands in the open card's draft. */
  filter: unknown;
  /** The repositories the Team attaches, the outer bound of any line. */
  teamRepos: readonly string[];
  disabled: boolean;
  onClose: () => void;
  /** Ran after a request lands, so the card can refresh its runs. */
  onAsked: () => void;
}

export function RunNowPicker({
  kind,
  deskId,
  filter,
  teamRepos,
  disabled,
  onClose,
  onAsked,
}: RunNowPickerProps) {
  const ask = pullRequestAskFor(kind);
  const repos = useMemo(() => lineRepos(filter, teamRepos), [filter, teamRepos]);
  const [raw, setRaw] = useState("");
  const [repo, setRepo] = useState<string>(repos.length === 1 ? repos[0]! : "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (!ask) return null;

  const prNumber = parsePrNumber(raw);
  // A pasted URL carries its own repository, and it wins: a person who pasted
  // a link has already said which repository they mean.
  const pastedRepo = parseRepoFromUrl(raw);
  const chosenRepo = pastedRepo ?? (repo || (repos.length === 1 ? repos[0]! : null));
  const repoIsThisLine =
    chosenRepo === null || repos.length === 0 || repos.includes(chosenRepo);

  const problem =
    raw.trim() === ""
      ? null
      : prNumber === null
        ? askUnreadable(ask.accepts)
        : !repoIsThisLine
          ? askNotThisLinesRepo(chosenRepo!, repos)
          : null;

  const send = async () => {
    if (prNumber === null || problem) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await ask.send({
        deskId,
        repo: chosenRepo,
        prNumber,
        raw: raw.trim(),
      });
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      if (!res.ok) {
        setMessage(body?.error ?? askUnreadable(ask.accepts));
        return;
      }
      setMessage(ASK_SENT);
      setRaw("");
      onAsked();
    } catch {
      setMessage(askUnreadable(ask.accepts));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tm-ask" data-testid="team-run-now-picker" data-act={kind.act}>
      <div className="tm-field">
        <label className="tm-field-label" htmlFor={`ask-pr-${deskId}`}>
          {ASK_PR_LABEL}
        </label>
        <input
          id={`ask-pr-${deskId}`}
          type="text"
          className="tm-input"
          data-testid="team-run-now-pr"
          disabled={disabled || busy}
          placeholder={
            ask.accepts === "number"
              ? ASK_PR_PLACEHOLDER_NUMBER
              : ASK_PR_PLACEHOLDER_URL
          }
          value={raw}
          onChange={(event) => {
            setRaw(event.target.value);
            setMessage(null);
          }}
        />
      </div>

      {/* Only when the line covers more than one and the input did not say. */}
      {repos.length > 1 && !pastedRepo && (
        <div className="tm-field">
          <label className="tm-field-label" htmlFor={`ask-repo-${deskId}`}>
            {ASK_REPO_LABEL}
          </label>
          <select
            id={`ask-repo-${deskId}`}
            className="tm-select"
            data-testid="team-run-now-repo"
            disabled={disabled || busy}
            value={repo}
            onChange={(event) => setRepo(event.target.value)}
          >
            <option value="">Choose a repository</option>
            {repos.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      {(problem || message) && (
        <p className="tm-message" role="status" data-testid="team-run-now-message">
          {problem ?? message}
        </p>
      )}

      <div className="tm-card-foot">
        <span className="tm-card-foot-spacer" />
        <button type="button" className="tm-quiet-btn" onClick={onClose}>
          {ASK_CANCEL}
        </button>
        <button
          type="button"
          className="tm-primary-btn"
          data-testid="team-run-now-send"
          disabled={
            disabled ||
            busy ||
            prNumber === null ||
            problem !== null ||
            (repos.length > 1 && !chosenRepo)
          }
          onClick={() => void send()}
        >
          {busy ? ASK_SENDING : ask.label}
        </button>
      </div>
    </div>
  );
}
